'use client';

import { useRef, useState } from 'react';
import { inspectMediaFile, type MediaInspectReport } from '@/lib/browser-media-inspect';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(ms: number | null) {
  if (ms === null) return 'Unknown';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const stamp = h > 0 ? `${h}:${String(m).padStart(2, '0')}` : `${m}`;
  return `${stamp}:${String(s).padStart(2, '0')}`;
}

// The report is the product here, so it has to be copyable as text. Someone checking a codec is
// usually about to paste it into a bug report or a message.
function reportToText(report: MediaInspectReport) {
  const lines = [
    `File: ${report.fileName}`,
    `Size: ${formatBytes(report.fileSize)}`,
    `Container: ${report.container ?? 'Unknown'}`,
    `Duration: ${formatDuration(report.durationMs)}`,
    `Overall bitrate: ${report.overallBitrate ?? 'Unknown'}`,
    '',
  ];

  for (const s of report.streams) {
    const parts = [
      `Stream ${s.index} (${s.kind}): ${s.codec}${s.profile ? ` (${s.profile})` : ''}`,
    ];
    if (s.resolution) parts.push(`  Resolution: ${s.resolution}`);
    if (s.displayAspectRatio) parts.push(`  Aspect ratio: ${s.displayAspectRatio}`);
    if (s.frameRate) parts.push(`  Frame rate: ${s.frameRate} fps`);
    if (s.pixelFormat) parts.push(`  Pixel format: ${s.pixelFormat}`);
    if (s.sampleRate) parts.push(`  Sample rate: ${s.sampleRate} Hz`);
    if (s.channels) parts.push(`  Channels: ${s.channels}`);
    if (s.bitrate) parts.push(`  Bitrate: ${s.bitrate}`);
    if (s.language) parts.push(`  Language: ${s.language}`);
    lines.push(...parts);
  }

  if (report.tags.length) {
    lines.push('', 'Metadata:');
    for (const t of report.tags) lines.push(`  ${t.name}: ${t.value}`);
  }

  return lines.join('\n');
}

// A worked example, shown before anything is dropped in.
//
// Measured lesson, not decoration: two tools that rendered an empty panel until a file arrived
// bounced at 55% and 35%, because a visitor who cannot see what they will get leaves. Showing a
// real report answers "is this the thing I wanted" before any upload.
const SAMPLE_REPORT: MediaInspectReport = {
  fileName: 'example-clip.mp4',
  fileSize: 4_812_544,
  container: 'mp4',
  durationMs: 32_400,
  overallBitrate: '1188 kb/s',
  streams: [
    {
      index: '0:0',
      kind: 'Video',
      codec: 'h264',
      profile: 'High',
      resolution: '1920x1080',
      pixelFormat: 'yuv420p',
      frameRate: '29.97',
      displayAspectRatio: '16:9',
      sampleRate: null,
      channels: null,
      bitrate: '1060 kb/s',
      language: null,
    },
    {
      index: '0:1',
      kind: 'Audio',
      codec: 'aac',
      profile: 'LC',
      resolution: null,
      pixelFormat: null,
      frameRate: null,
      displayAspectRatio: null,
      sampleRate: '48000',
      channels: 'stereo',
      bitrate: '128 kb/s',
      language: 'eng',
    },
  ],
  tags: [
    { name: 'major_brand', value: 'isom' },
    { name: 'creation_time', value: '2026-08-14T09:12:40.000000Z' },
    { name: 'encoder', value: 'Lavf61.7.100' },
  ],
};

export default function BrowserMediaInspector() {
  const [report, setReport] = useState<MediaInspectReport>(SAMPLE_REPORT);
  const [isSample, setIsSample] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setError(null);
    setCopied(false);
    setIsBusy(true);

    try {
      await consumeToolUsage(
        'video-metadata-viewer',
        'Credits are not available for another inspection right now.',
      );
      setReport(await inspectMediaFile(file));
      setIsSample(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'This file could not be read.');
    } finally {
      setIsBusy(false);
    }
  }

  async function copyReport() {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(reportToText(report));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Copying failed. Your browser blocked clipboard access.');
    }
  }

  const rows: [string, string][] = [
    ['File', report.fileName],
    ['Size', formatBytes(report.fileSize)],
    ['Container', report.container ?? 'Unknown'],
    ['Duration', formatDuration(report.durationMs)],
    ['Overall bitrate', report.overallBitrate ?? 'Unknown'],
    ['Streams', String(report.streams.length)],
  ];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Inspect</span>
        <h2 className="tool-live-title">Video metadata viewer</h2>
        <p className="tool-live-subtitle">
          Drop a video, audio or image file and read its codec, resolution, frame rate, bitrate and
          tags. The file never leaves your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Choose a file or drop it here</span>
            <input
              accept="video/*,audio/*,image/*,.mkv,.mov,.webm,.avi,.flv,.wmv,.ts,.m2ts,.opus,.amr,.caf,.wma,.heic"
              onChange={(e) => handleFile(e.target.files?.[0])}
              ref={inputRef}
              type="file"
            />
            <small>
              Nothing is uploaded. The file is read locally with ffmpeg compiled to WebAssembly,
              which is why the first inspection takes a moment to start.
            </small>
          </label>

          {isBusy ? <p className="tool-live-panel-note">Reading the file…</p> : null}
          {error ? (
            <p className="tool-live-panel-note" role="alert">
              {error}
            </p>
          ) : null}

          <div className="tool-live-actions tool-live-field-full">
            <button className="button button-secondary" onClick={copyReport} type="button">
              {copied ? 'Copied' : 'Copy report'}
            </button>
          </div>
        </section>

        <section className="tool-live-preview">
          {isSample ? (
            <p className="tool-live-panel-note">
              Sample report. Drop your own file and this is replaced by its real numbers.
            </p>
          ) : null}
          <>
            <dl className="tool-live-stats">
              {rows.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>

            {report.streams.map((s) => (
              <div className="detail-table-scroll" key={s.index}>
                <table className="detail-table">
                  <caption>
                    Stream {s.index} · {s.kind} · {s.codec}
                    {s.profile ? ` (${s.profile})` : ''}
                  </caption>
                  <tbody>
                    {(
                      [
                        ['Codec', s.codec],
                        ['Profile', s.profile],
                        ['Resolution', s.resolution],
                        ['Aspect ratio', s.displayAspectRatio],
                        ['Frame rate', s.frameRate ? `${s.frameRate} fps` : null],
                        ['Pixel format', s.pixelFormat],
                        ['Sample rate', s.sampleRate ? `${s.sampleRate} Hz` : null],
                        ['Channels', s.channels],
                        ['Bitrate', s.bitrate],
                        ['Language', s.language],
                      ] as [string, string | null][]
                    )
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <tr key={label}>
                          <th scope="row">{label}</th>
                          <td>{value}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ))}

            {report.tags.length ? (
              <div className="detail-table-scroll">
                <table className="detail-table">
                  <caption>Container metadata</caption>
                  <tbody>
                    {report.tags.map((t) => (
                      <tr key={t.name}>
                        <th scope="row">{t.name}</th>
                        <td>{t.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        </section>
      </div>
    </div>
  );
}
